import type DevExpress from 'devextreme/bundles/dx.all';
import CustomStore from 'devextreme/data/custom_store';
import * as ko from 'knockout';
import { DxWidget } from '../../../AbstractWidget';
import { AS, datagrid, datagridButton, refreshDx } from '../../../dx_helper';
import * as TRANSLATIONS from '../../../enums/translations';
import { DONE } from '../../../helper';
import * as API from '../../../its-itembank-api.g';
import { ServerConnection } from '../../RestAPI';
import * as ROUTES from '../../routes';
import { registerOnUpdateToolbar } from '../../toptoolbar.service';
import { superuser } from '../../ui-routes';
import { UIAction } from '../../uiAction';
import * as i18next from './../../../i18n/i18n';
import { IParams, WIDGET_NAME } from './route';
import { htmlString } from './widget.html.g';

type Q = Awaited<ReturnType<API.Sdk['ui_superuser_profileoverview_init']>>;
type PROFILE = Q['TestDefinitionProfile']['all'][0];

export class ViewModel extends DxWidget {
    public readonly loaded = ko.observable(false);

    public readonly actionEdit = new UIAction<PROFILE>(undefined, async (e, args) => {
        await ROUTES.routeManager.navigateToHREF(superuser.ProfileEdit.href({
            profile: args.docReferenceId
        }));
    });
    public readonly actionCopy = new UIAction<PROFILE>(undefined, async (e, args) => {
        const newExamOrder = await ServerConnection.api.ui_superuser_profileoverview_copy({
            docRef: args.docReferenceId
        });
        const profile = newExamOrder.TestDefinitionProfile.copy.docReferenceId;
        await ROUTES.routeManager.navigateToHREF(superuser.ProfileEdit.href({
            profile
        }));
    });




    constructor(readonly params: IParams) {
        super();
    }

    private readonly _subjects: PROFILE[] = [];

    public async OnRefresh() {
        await super.OnRefresh();
        const r = await ServerConnection.api.ui_superuser_profileoverview_init({});
        this._subjects.splice(0, this._subjects.length, ...r.TestDefinitionProfile.all);
        await refreshDx(this);
    }

    public readonly gridOptions = ko.pureComputed(() => {
        const retVal = datagrid({
            WIDGET_NAME,
            widget: this,
            config: {
                allowColumnResizing: true,
                sorting: {
                    mode: 'single',
                },
                searchPanel: {
                    visible: true
                },
                filterPanel: {
                    visible: true
                },
                selection: {
                    mode: 'single'
                },
                groupPanel: {
                    visible: false
                },
                dataSource: {
                    store: new CustomStore({
                        loadMode: 'raw',
                        load: async () => {
                            return this._subjects;
                        },
                        byKey: async key => {
                            return this._subjects.find(x => x.docReferenceId === key);
                        }
                    })
                }
            }
        });

        retVal.columns.push({
            caption: i18next.t(['ui.superuser.profileoverview.TITLE']),
            dataField: 'title',
            sortOrder: 'asc',
            sortIndex: 0,
            dataType: 'string',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.profileoverview.ID']),
            dataField: 'id',
            dataType: 'string',
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.profileoverview.LAYOUT']),
            dataType: 'string',
            calculateDisplayValue: x => {
                const queryRow: PROFILE = x;
                return i18next.t(TRANSLATIONS.ItemHost[queryRow.itemHost]);
            }
        });
        retVal.columns.push({
            caption: i18next.t(['ui.superuser.profileoverview.DEACTIVATED']),
            dataField: 'isDeleted',
            dataType: 'boolean',
        });

        retVal.columns.push({
            caption: i18next.t(['ui.superuser.profileoverview.ACTIONS']),
            type: 'buttons',
            buttons: [
                datagridButton({
                    text: i18next.t(['ui.superuser.profileoverview.EDIT']),
                    action: this.actionEdit,
                }),
                datagridButton({
                    text: i18next.t(['ui.superuser.profileoverview.COPY']),
                    action: this.actionCopy,
                })
            ]
        });
        retVal.filterValue = ['isDeleted', '=', false];
        return retVal;
    });

    public readonly actionNewSubject = new UIAction(undefined, async () => {
        const r = await ServerConnection.api.ui_superuser_profileoverview_newprofile({});

        const profile = r.TestDefinitionProfile.new.docReferenceId;
        await ROUTES.routeManager.navigateToHREF(superuser.ProfileEdit.href({
            profile,
        }));
    });


    private onPrepareTopToolbar(toolbar: DevExpress.ui.dxToolbar.Properties) {
        toolbar.items.push(AS<DevExpress.ui.dxToolbarItemTemplate>({
            location: 'after',
            widget: 'dxButton',
            options: AS<DevExpress.ui.dxButton.Properties>({
                icon: 'plus',
                text: i18next.t(['ui.superuser.profileoverview.NEW']),
                onClick: this.actionNewSubject.click
            })
        }));
        return DONE;
    }


    public async initialize() {
        await super.initialize();
        await this.OnRefresh();
        this.disposables.addDiposable(registerOnUpdateToolbar(x => this.onPrepareTopToolbar(x)));
        this.loaded(true);
    }
}

export function create(params: IParams, componentInfo: ko.components.ComponentInfo) {
    const retVal = new ViewModel(params);
    retVal.DoInit({ WIDGET_NAME });
    return retVal;
}

ko.components.register(WIDGET_NAME, {
    viewModel: {
        createViewModel: create
    },
    template: htmlString.replace(/@@@/g, WIDGET_NAME)
});
